"use client";

import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function InvestmentExtensionGuide() {
  const {
    ref: headlineRef,
    isVisible: headlineVisible
  } = useRevealOnScroll<HTMLHeadingElement>({
    threshold: 0.3
  });
  const {
    ref: subRef,
    isVisible: subVisible
  } = useRevealOnScroll<HTMLParagraphElement>({
    threshold: 0.3
  });
  const {
    ref: ctaRef,
    isVisible: ctaVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.3
  });
  const {
    ref: imgRef,
    isVisible: imgVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  return <section data-section-id="3000" id="investment-extension-guide" className="bg-background text-foreground relative overflow-hidden py-32 md:py-48 lg:py-56 px-6 md:px-12">
      {}
      <div aria-hidden="true" className="pointer-events-none absolute -bottom-16 -right-8 md:-right-4 select-none font-serif font-light leading-none opacity-[0.06] text-foreground" style={{
      fontSize: "clamp(280px, 40vw, 460px)"
    }}>
        VS
      </div>

      {}
      <div aria-hidden="true" className="pointer-events-none absolute left-4 md:left-12 top-1/2 -translate-y-1/2 hidden md:block opacity-30">
        <svg width="180" height="220" viewBox="0 0 180 220" fill="none" xmlns="http://www.w3.org/2000/svg" className="text-foreground">
          <rect x="20" y="40" width="60" height="140" stroke="currentColor" strokeWidth="0.8" strokeDasharray="3 3" />
          <rect x="80" y="60" width="80" height="100" stroke="currentColor" strokeWidth="0.8" />
          <path d="M80 80 L100 80 L100 100 L80 100" stroke="currentColor" strokeWidth="0.8" />
          <path d="M80 120 L100 120 L100 140 L80 140" stroke="currentColor" strokeWidth="0.8" />
          <path d="M100 80 L100 140" stroke="currentColor" strokeWidth="0.8" />
          <path d="M20 40 L20 20 L160 20" stroke="currentColor" strokeWidth="0.6" strokeDasharray="2 2" />
          <path d="M20 180 L20 200 L160 200" stroke="currentColor" strokeWidth="0.6" strokeDasharray="2 2" />
          <text x="30" y="215" fill="currentColor" fontSize="6" fontFamily="monospace" opacity="0.7">
            VELVET · GUIDE 01
          </text>
          <circle cx="90" cy="110" r="2" fill="currentColor" />
          <line x1="90" y1="110" x2="150" y2="110" stroke="currentColor" strokeWidth="0.4" />
          <text x="120" y="108" fill="currentColor" fontSize="5" fontFamily="monospace">
            STYLE
          </text>
        </svg>
      </div>

      <div className="relative mx-auto max-w-7xl">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-8 items-center">
          {}
          <div className="lg:col-span-8 text-center lg:text-left">
            {}
            <p className="font-sans text-[12px] uppercase tracking-[0.25em] text-foreground/70 mb-10">
              The Velvet Standard
            </p>

            {}
            <h2 ref={headlineRef} className="font-default font-light text-4xl sm:text-5xl md:text-6xl lg:text-7xl xl:text-[88px] leading-[1.05] tracking-tight text-foreground">
              <span className="inline-block transition-all duration-1000 ease-out" style={{
              opacity: headlineVisible ? 1 : 0,
              transform: headlineVisible ? "translateY(0)" : "translateY(24px)",
              transitionDelay: "0ms"
            }}>
                Elevate your look.
              </span>
              <br />
              <span className="inline-block transition-all duration-1000 ease-out" style={{
              opacity: headlineVisible ? 1 : 0,
              transform: headlineVisible ? "translateY(0)" : "translateY(24px)",
              transitionDelay: "200ms"
            }}>
                Download our <em className="italic font-normal text-primary">pricing</em> guide.
              </span>
            </h2>

            {}
            <p ref={subRef} className="font-default text-lg md:text-[19px] leading-relaxed text-foreground/75 mt-8 max-w-xl mx-auto lg:mx-0 transition-all duration-1000 ease-out" style={{
            opacity: subVisible ? 1 : 0,
            transform: subVisible ? "translateY(0)" : "translateY(16px)",
            transitionDelay: "400ms"
          }}>
              Explore our full range of precision cuts, bespoke color services, and premium extension options.
            </p>

            {}
            <div ref={ctaRef} className="mt-12 flex flex-col sm:flex-row items-center lg:items-center justify-center lg:justify-start gap-6 sm:gap-8 transition-all duration-1000 ease-out" style={{
            opacity: ctaVisible ? 1 : 0,
            transform: ctaVisible ? "translateY(0)" : "translateY(16px)",
            transitionDelay: "600ms"
          }}>
              <Button asChild size="lg" className="bg-primary text-primary-foreground hover:bg-primary/90 font-sans text-sm tracking-wide uppercase px-8 py-6 h-auto transition-colors duration-500">
                <Link to="/BookPage">Download Guide</Link>
              </Button>

              <Link to="/BookPage" className="font-default text-foreground underline underline-offset-[6px] decoration-[0.5px] decoration-foreground/40 hover:decoration-foreground transition-colors text-base tracking-wide">
                Book a Consultation →
              </Link>
            </div>

            {}
            <p className="font-sans text-xs uppercase tracking-[0.2em] text-foreground/50 mt-14">
              Tue–Sat · 10am — 7pm · By Appointment
            </p>
          </div>

          {}
          <div ref={imgRef} className="lg:col-span-4 flex justify-center lg:justify-end transition-all duration-1200 ease-out" style={{
          opacity: imgVisible ? 1 : 0,
          transform: imgVisible ? "translateY(0) rotate(0deg)" : "translateY(40px) rotate(-2deg)",
          transitionDelay: "300ms"
        }}>
            <div className="relative w-[220px] sm:w-[260px] md:w-[280px] aspect-[3/4] overflow-hidden">
              <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1701490/img/salon_styling_session.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1701490/img/salon_styling_session.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1701490/img/salon_styling_session.png-wvc-sizes" alt="Professional stylist working on a modern haircut in a boutique salon" loading="lazy" decoding="async" className="w-full h-full object-cover" />
              <div className="absolute bottom-0 left-0 right-0 p-4 bg-gradient-to-t from-black/40 to-transparent">
                <span className="font-sans text-[10px] uppercase tracking-[0.3em] text-white/90">
                  Velvet · Shears
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>;
}